import { useEffect, useRef, useState } from "react";
import {
  MapContainer as LeafletMap,
  TileLayer,
  Marker,
  Popup,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useTranslation } from "react-i18next";
import "../../styles/components/LocationPickerMap.css";
import AppBackButton from "../common/AppBackButton";

const WORLD_BOUNDS = [
  [-85, -180],
  [85, 180],
];

/* ================= LEAFLET ICON FIX ================= */

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

/* ================= SEARCH BAR ================= */

function SearchControl({ onBack }) {
  const { t } = useTranslation();
  const backLabel = t("back", { defaultValue: "Back" }).replace(
    /^[<\-\u2013\u2014\u2190\u2B05\s]+/,
    ""
  );
  const map = useMap();
  const containerRef = useRef(null);

  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (containerRef.current) {
      L.DomEvent.disableClickPropagation(containerRef.current);
      L.DomEvent.disableScrollPropagation(containerRef.current);
    }
  }, []);

  const search = (value) => {
    setQuery(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      if (value.length < 3) {
        setResults([]);
        return;
      }

      if (abortRef.current) {
        abortRef.current.abort();
      }

      abortRef.current = new AbortController();

      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            value
          )}&limit=6&accept-language=en`,
          { signal: abortRef.current.signal }
        );

        const data = await res.json();
        setResults(data);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Search error", err);
        }
      }
    }, 300);
  };

  const flyToResult = (place) => {
    const lat = Number(place.lat);
    const lng = Number(place.lon);

    let zoom = 10;
    if (place.class === "boundary") zoom = 5;
    else if (place.type === "state" || place.type === "region") zoom = 7;
    else if (place.type === "city") zoom = 11;
    else if (place.type === "town") zoom = 13;
    else zoom = 16;

    map.flyTo([lat, lng], zoom, { duration: 1.2 });
    setQuery(place.display_name);
    setResults([]);
  };

  return (
    <div ref={containerRef} className="map-picker-search-control">
      <input
        type="text"
        value={query}
        placeholder={t("map_picker_search_placeholder")}
        onChange={(e) => search(e.target.value)}
        className="map-picker-search-input"
      />

      <AppBackButton
        className="map-picker-back-btn"
        label={backLabel}
        onClick={onBack}
      />

      {results.length > 0 && (
        <ul className="map-picker-search-results">
          {results.map((r, i) => (
            <li
              key={i}
              onClick={() => flyToResult(r)}
              className="map-picker-search-result-item"
            >
              {r.display_name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ================= MAP CLICK EVENTS ================= */

function PickerEvents({ setTempLocation }) {
  useMapEvents({
    click(e) {
      setTempLocation(e.latlng);
    },
  });
  return null;
}

/* ================= MAIN MAP PICKER ================= */

export default function MapPicker() {
  const { t } = useTranslation();
  const [tempLocation, setTempLocation] = useState(null);
  const markerRef = useRef(null);

  const goBack = () => {
    window.dispatchEvent(new CustomEvent("returnToPost"));
    setTempLocation(null);
  };

  const confirmLocation = () => {
    if (!tempLocation) return;

    window.dispatchEvent(
      new CustomEvent("mapLocationPicked", {
        detail: {
          lat: tempLocation.lat,
          lng: tempLocation.lng,
        },
      })
    );

    goBack();
  };

  useEffect(() => {
    if (!tempLocation || !markerRef.current) return;

    const rafId = requestAnimationFrame(() => {
      markerRef.current?.openPopup();
    });

    return () => cancelAnimationFrame(rafId);
  }, [tempLocation]);

  return (
    <LeafletMap
      center={[20, 0]}
      zoom={2}
      minZoom={2}
      maxBounds={WORLD_BOUNDS}
      maxBoundsViscosity={1.0}
      style={{ width: "100%", height: "100%" }}
    >
      <TileLayer
        attribution="&copy; OpenStreetMap contributors"
        url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
        noWrap={true}
      />

      <SearchControl onBack={goBack} />
      <PickerEvents setTempLocation={setTempLocation} />

      {tempLocation && (
        <Marker
          position={tempLocation}
          ref={(instance) => {
            if (instance) markerRef.current = instance;
          }}
        >
          <Popup autoClose={false} closeOnClick={false}>
            <div className="map-picker-popup">
              <div className="map-picker-popup-coords">
                {"\u{1F4CD}"} {tempLocation.lat.toFixed(5)}, {tempLocation.lng.toFixed(5)}
              </div>

              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  confirmLocation();
                }}
                className="map-picker-popup-confirm-btn"
              >
                {"\u2705"} {t("map_picker_confirm")}
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setTempLocation(null);
                }}
                className="map-picker-popup-cancel-btn"
              >
                {"\u274C"} {t("map_picker_cancel")}
              </button>
            </div>
          </Popup>
        </Marker>
      )}
    </LeafletMap>
  );
}
